import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { DataTableComponent, TableColumn, TableConfig } from '@components/data-table/data-table.component';
import { Loading } from '@components/ui/loading/loading';
import { CopyableIdentifierComponent } from '@app/pages/admin/components/copyable-identifier/copyable-identifier';
import { OperationMonitoringService } from '@6grain/masofa-monolith-api-client/common/api/operationMonitoring.service';
import { GeoServerMonitoringSnapshot } from '@6grain/masofa-monolith-api-client/common/model/geoServerMonitoringSnapshot';

@Component({
  selector: 'app-orchestration-geoserver-page',
  standalone: true,
  imports: [CommonModule, TranslateModule, SecondaryButton, DataTableComponent, Loading, CopyableIdentifierComponent],
  templateUrl: './orchestration-geoserver-page.html',
  styleUrl: './orchestration-geoserver-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrchestrationGeoServerPageComponent implements OnInit, OnDestroy {
  private readonly api = inject(OperationMonitoringService);
  private readonly destroy$ = new Subject<void>();

  readonly loading = signal(false);
  readonly errorMessage = signal('');
  readonly snapshot = signal<GeoServerMonitoringSnapshot | null>(null);

  readonly columns: TableColumn[] = [
    { field: 'name', header: 'GEOSERVER_WORKSPACES' },
    { field: 'layerCount', header: 'GEOSERVER_LAYERS' },
    { field: 'coverageStoreCount', header: 'GEOSERVER_COVERAGE_STORES' },
    { field: 'dataStoreCount', header: 'GEOSERVER_DATA_STORES' },
  ];

  readonly tableConfig = computed<TableConfig>(() => ({
    paginator: false,
    emptyMessage: 'GEOSERVER_MONITOR_NO_WORKSPACES',
  }));

  readonly reachableLabel = computed(() => (this.snapshot()?.reachable ? 'Yes' : 'No'));
  readonly workspaces = computed(() => this.snapshot()?.workspaces ?? []);
  readonly workspaceRows = computed(() => this.workspaces().length);
  readonly tileStatus = computed(() => this.snapshot()?.tileProbe?.status ?? '—');
  readonly tileError = computed(() => this.snapshot()?.tileProbe?.error ?? '');
  readonly modules = computed(() => this.snapshot()?.modules ?? []);
  readonly services = computed(() => this.snapshot()?.services ?? []);
  readonly stores = computed(() => this.snapshot()?.stores ?? []);
  readonly tileProbes = computed(() => this.snapshot()?.tileProbes ?? []);
  readonly systemStatus = computed(() => this.snapshot()?.systemStatus ?? []);
  readonly moduleRows = computed(() => this.modules().length);
  readonly serviceRows = computed(() => this.services().length);
  readonly storeRows = computed(() => this.stores().length);
  readonly probeRows = computed(() => this.tileProbes().length);
  readonly metricRows = computed(() => this.systemStatus().length);

  readonly moduleColumns: TableColumn[] = [
    { field: 'name', header: 'GEOSERVER_MODULES' },
    { field: 'status', header: 'Status' },
    { field: 'version', header: 'GEOSERVER_VERSION' },
    { field: 'message', header: 'Message' },
  ];
  readonly serviceColumns: TableColumn[] = [
    { field: 'name', header: 'GEOSERVER_SERVICES' },
    { field: 'enabled', header: 'GEOSERVER_ENABLED', type: 'boolean' },
    { field: 'maxRenderingTime', header: 'Max rendering time' },
    { field: 'maxRenderingErrors', header: 'Max rendering errors' },
    { field: 'maxRequestMemory', header: 'Max request memory' },
  ];
  readonly storeColumns: TableColumn[] = [
    { field: 'workspace', header: 'GEOSERVER_WORKSPACES' },
    { field: 'kind', header: 'Kind' },
    { field: 'name', header: 'Name' },
    { field: 'type', header: 'GEOSERVER_TYPE' },
    { field: 'enabled', header: 'GEOSERVER_ENABLED', type: 'boolean' },
    { field: 'path', header: 'GEOSERVER_PATH' },
  ];
  readonly probeColumns: TableColumn[] = [
    { field: 'status', header: 'Status' },
    { field: 'workspace', header: 'GEOSERVER_WORKSPACES' },
    { field: 'layer', header: 'GEOSERVER_LAYERS' },
    { field: 'httpStatus', header: 'HTTP' },
    { field: 'contentType', header: 'Content type' },
    { field: 'byteCount', header: 'Bytes' },
    { field: 'durationMs', header: 'ms' },
    { field: 'error', header: 'Error' },
  ];
  readonly metricColumns: TableColumn[] = [
    { field: 'name', header: 'Name' },
    { field: 'value', header: 'Value' },
  ];

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(): void {
    if (this.loading()) return;
    this.loading.set(true);
    this.errorMessage.set('');
    this.api.monitoringGeoserverGet().pipe(takeUntil(this.destroy$)).subscribe({
      next: snapshot => {
        this.snapshot.set(snapshot);
        this.loading.set(false);
      },
      error: () => {
        this.snapshot.set(null);
        this.errorMessage.set('GEOSERVER_MONITOR_LOAD_ERROR');
        this.loading.set(false);
      },
    });
  }
}
